import React from 'react'
import { Divider } from '../../components/simple/divider'
import {
  IconCaretRight,
  IconCaretUp,
  IconCircle,
  IconEdit,
  IconEmail,
  IconPhone,
  IconSignIn,
  IconTimes,
  IconTrash,
  IconTrashRestore,
} from '../../icons/library'
import { SimpleUser, Teacher } from '../../providers/context'
import { GenericContact, UsageRow } from '../familyContacts/contactComponents'
import { UsageData } from '../familyContacts/familyContactData'
import { RemoteHTMLTooltip, Tooltip } from '../../components/simple/tooltip'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { formatPhoneNumber, formatPhoneNumberForEditing, pluralize } from '../../utils'
import { Button } from '../../components/simple/button'
import { EditResponse, showEditErrors } from '../modals/editUser'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { Popups } from '../../providers/popups'
import { TextInput } from '../../components/form/textInput'
import { LabeledCheckbox } from '../../components/form/checkbox'
import { getDirectLink, URLAction } from '../../providers/URLParams'
import { SmartLink } from '../../components/simple/link'

// Function to check if current user can edit a teacher contact
const canEditTeacherContact = (currentUser: Teacher, targetTeacher: TeacherContact): boolean => {
  // Allow specific ReachMyTeach admin emails
  if (currentUser.email === 'jeff@reachmyteach.com' || currentUser.email === 'helen@reachmyteach.com') {
    return true
  }

  // Only admin+ can edit (admin level 1 or 2)
  // Only teachers can edit (non-teachers cannot edit unless it's themselves)
  if (currentUser.type !== 'teacher' && currentUser.id !== targetTeacher.id) {
    return false
  }

  // Self-editing is always allowed
  if (currentUser.id === targetTeacher.id) {
    // Allow self-editing
    return true
  }
  if (currentUser.admin < 1) {
    return false
  }

  if (currentUser.admin === 2 && currentUser.districtAccess && !targetTeacher.districtAccess) {
    // System administrators can edit anyone
    return true
  }

  // Can only edit users with lower admin status
  if (targetTeacher.admin >= currentUser.admin) {
    return false
  }

  // School admins (admin level 1) can only edit users at schools they are at
  if (currentUser.admin === 1) {
    const currentUserSchoolNames = currentUser.schools.map((school) => school.name)
    const targetTeacherSchools = targetTeacher.schools || []

    // Check if there's any overlap between current user's schools and target teacher's schools
    return currentUserSchoolNames.some((schoolName) => targetTeacherSchools.includes(schoolName))
  }

  return currentUser.admin > targetTeacher.admin
}

export type TeacherContact = {
  teacherType?: string
  email: string
  forwardMessagesToEmail: boolean
  forwardSharedMessagesToEmail: boolean
  phoneData: {
    phoneNumber: string
    status: 'reachable' | 'landline' | 'bounced' | 'empty'
    receiveCalls: boolean
  }
  districtAccess: boolean
  schools: string[]
  admin: number
  teacherGroups: string[]
  usage: UsageData
  authUserCanSignInAs: boolean
  classes: {
    id: number
    name: string
    studentCount: number
  }[]
  externalStaff: boolean
  editable: boolean
  deletable: boolean
  deleted: boolean
  editSchoolOptions: {
    id: number
    name: string
    atSchool: boolean
    editable: boolean
  }[]
  showGradeGroups: boolean
  showAllClasses: boolean
  showAttendance: boolean
  broadcastAdmin: boolean
  lexikeetAdmin: boolean
  atLexikeetSchool: boolean
  canEditExternalStaff: boolean
  ptcAdmin: boolean
  assessmentAdmin: boolean
  preferredName?: string
  originalName: string
} & SimpleUser

export const TeacherContactRow: React.FC<{
  teacher: TeacherContact
  reloadContacts: () => void
  user: Teacher
}> = ({ teacher, reloadContacts, user }) => {
  const [editing, setEditing] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [classesOpen, setClassesOpen] = React.useState(false)
  const displayPhoneNumber = formatPhoneNumber(teacher.phoneData.phoneNumber, true)

  // Check if current user can edit this teacher contact
  const canEdit = canEditTeacherContact(user, teacher)

  const [email, setEmail] = React.useState(teacher.email)
  const [emailInvalid, setEmailInvalid] = React.useState(false)
  const [phoneNumber, setPhoneNumber] = React.useState(formatPhoneNumberForEditing(teacher.phoneData.phoneNumber))
  const [phoneNumberInvalid, setPhoneNumberInvalid] = React.useState(false)
  const [schools, setSchools] = React.useState(
    new Set(teacher.editSchoolOptions?.filter((s) => s.atSchool).map((s) => s.id) || [])
  )
  const [schoolEditOpen, setSchoolEditOpen] = React.useState(false)
  const [admin, setAdmin] = React.useState(teacher.admin)
  const [districtAccess, setDistrictAccess] = React.useState(teacher.districtAccess)
  const [showAllClasses, setShowAllClasses] = React.useState(teacher.showAllClasses)
  const [showGradeGroups, setShowGradeGroups] = React.useState(teacher.showGradeGroups)
  const [showAttendance, setShowAttendance] = React.useState(teacher.showAttendance)
  const [broadcastAdmin, setBroadcastAdmin] = React.useState(teacher.broadcastAdmin)
  const [lexikeetAdmin, setLexikeetAdmin] = React.useState(teacher.lexikeetAdmin)
  const [ptcAdmin, setPtcAdmin] = React.useState(teacher.ptcAdmin)
  const [assessmentAdmin, setAssessmentAdmin] = React.useState(teacher.assessmentAdmin)
  const [externalStaff, setExternalStaff] = React.useState(teacher.externalStaff)
  const resetEdit = () => {
    setEmail(teacher.email)
    setEmailInvalid(false)
    setPhoneNumber(formatPhoneNumberForEditing(teacher.phoneData.phoneNumber))
    setPhoneNumberInvalid(false)
    setSchools(new Set(teacher.editSchoolOptions?.filter((s) => s.atSchool).map((s) => s.id) || []))
    setShowGradeGroups(teacher.showGradeGroups)
    setShowAttendance(teacher.showAttendance)
    setBroadcastAdmin(teacher.broadcastAdmin)
    setLexikeetAdmin(teacher.lexikeetAdmin)
    setPtcAdmin(teacher.ptcAdmin)
    setAssessmentAdmin(teacher.assessmentAdmin)
    setAdmin(teacher.admin)
    setDistrictAccess(teacher.districtAccess)
    setShowAllClasses(teacher.showAllClasses)
    setLoading(false)
  }
  React.useEffect(resetEdit, [teacher])

  const canEditAdmin =
    canEdit && (user.admin === 2 || (user.admin === 1 && user.districtAccess && !districtAccess)) && !externalStaff
  const canEditAdminPlus = canEditAdmin && user.districtAccess && (!districtAccess || user.systemAdministrator)
  const canEditDistrictAccess = canEdit && user.districtAccess && (user.admin > admin || user.systemAdministrator)
  const canEditSchools = canEdit && user.admin === 2 && user.districtAccess && !districtAccess && admin < 2

  console.log(
    'districtAccess:',
    districtAccess,
    'schools.size:',
    schools.size,
    'user.districtAccess:',
    user.districtAccess,
    'user.admin:',
    user.admin,
    'admin:',
    admin,
    'user.systemAdministrator:',
    user.systemAdministrator,
    'canEditDistrictAccess:',
    canEditDistrictAccess
  )

  const edit = () => {
    // Double-check permissions before allowing edit
    if (!canEdit) {
      return
    }
    resetEdit()
    setEditing(true)
  }
  const saveEdit = async () => {
    // Double-check permissions before saving
    if (!canEdit) {
      return
    }
    setLoading(true)
    setEmailInvalid(false)
    setPhoneNumberInvalid(false)
    try {
      const response = await makeRequest<EditResponse>('/frontend_api/editUser', {
        id: teacher.id,
        email,
        phoneNumber,
        schools: canEditSchools ? schools : undefined,
        admin: Math.min(admin, canEditAdminPlus ? 2 : canEditAdmin ? 1 : 0),
        districtAccess: canEditDistrictAccess ? districtAccess : undefined,
        showAllClasses: teacher.schools.length > 0 ? showAllClasses : undefined,
        showGradeGroups,
        showAttendance: user.district.attendanceEnabled ? showAttendance : undefined,
        broadcastAdmin,
        LKAdmin: lexikeetAdmin,
        PTCAdmin: ptcAdmin,
        assessmentAdmin: assessmentAdmin,
        externalStaff: teacher.canEditExternalStaff ? externalStaff && admin === 0 : undefined,
      })
      if (response === 'success') {
        setEditing(false)
        reloadContacts()
        // loading is reset when the reload completes
      } else {
        showEditErrors(response, setPhoneNumberInvalid, setEmailInvalid)
        setLoading(false)
      }
    } catch (e) {
      showAxiosErrorAsToast(e)
      setLoading(false)
    }
  }

  const toggleActive = async (active: boolean) => {
    // Double-check permissions before toggling active status
    if (!canEdit) {
      return
    }
    const msg = active ? 'Are you sure you want to activate this user' : 'Are you sure you want to deactivate this user'
    if (await Popups.simpleConfirm(msg)) {
      setLoading(true)
      makeRequest('/frontend_api/editUser', {
        id: teacher.id,
        active,
      })
        .then(reloadContacts)
        .catch(showAxiosErrorAsToast)
        .finally(() => setLoading(false))
    }
  }

  return (
    <GenericContact
      className="max-w-[650px] self-stretch"
      loading={loading}
      avatar={teacher.avatar}
      deleted={teacher.deleted}
      title={
        <>
          <div className="flex flex-col pr-2 leading-5">
            <SmartLink
              className="cursor-pointer font-bold no-underline"
              to={getDirectLink({
                [URLAction.OpenInboxTab]: 'sent',
                [URLAction.SetSearchUser]: { id: teacher.id, name: teacher.name },
                [URLAction.SetAllSchoolMessages]: user.admin > 0,
              })}
            >
              {teacher.name}
              {teacher.preferredName && (
                <IconCircle
                  className="ml-1 text-warning"
                  size="xs"
                  tooltip={`Name from SIS: ${teacher.originalName}`}
                />
              )}
            </SmartLink>
            {teacher.teacherType && (
              <div className="flex items-center text-sm text-gray-500">
                {teacher.teacherType}
                {canEdit && (
                  <IconTimes
                    className="ml-1 cursor-pointer text-red-500"
                    size="sm"
                    tooltip="Clear this teacher type label"
                    onClick={() => {
                      setLoading(true)
                      makeRequest('/frontend_api/editUser', {
                        id: teacher.id,
                        teacherType: null,
                      })
                        .then(reloadContacts)
                        .catch(showAxiosErrorAsToast)
                    }}
                  />
                )}
              </div>
            )}
          </div>
          {teacher.externalStaff && (
            <Badge
              badgeStyle={BadgeStyle.warning}
              tooltip="This user is not a teacher, and does not have full access to our site. They can send messages to students in their assigned groups."
            >
              external staff
            </Badge>
          )}
          {teacher.districtAccess ? (
            <Badge
              badgeStyle={BadgeStyle.mint}
              tooltip={`District access${teacher.schools.length > 0 ? ` and at ${teacher.schools.length} ${pluralize(teacher.schools.length, 'school')}: ${teacher.schools.join(', ')}` : ''}`}
            >
              District{teacher.schools.length > 0 && ` + ${teacher.schools.length}`}
            </Badge>
          ) : teacher.schools.length === 1 ? (
            <Badge badgeStyle={BadgeStyle.mint}>{teacher.schools[0]}</Badge>
          ) : teacher.schools.length > 1 ? (
            <Badge tooltip={teacher.schools.join(', ')} badgeStyle={BadgeStyle.mint}>
              {teacher.schools.length}
            </Badge>
          ) : null}
          {teacher.admin > 0 && (
            <Badge badgeStyle={BadgeStyle.blue} tooltip="This user is an admin">
              {teacher.admin === 2 ? 'A+' : 'A'}
            </Badge>
          )}
          {teacher.teacherGroups.length > 0 && (
            <Badge badgeStyle={BadgeStyle.light} tooltip={`In teacher groups: ${teacher.teacherGroups.join(', ')}`}>
              {teacher.teacherGroups.length}
            </Badge>
          )}
          <UsageRow
            data={teacher.usage}
            deliveryStatusID={user.systemAdministrator ? teacher.id : undefined}
            className="rounded bg-gray-50 px-2"
          />
          <div className="flex-grow"></div>
          {!editing && teacher.authUserCanSignInAs && (
            <SmartLink to={`/admin/login/${teacher.id}`}>
              <IconSignIn className="text-charcoal-text" tooltip={`Login as ${teacher.name}`} />
            </SmartLink>
          )}
          {editing ? (
            <>
              <Button logo small onClick={saveEdit}>
                Save
              </Button>
              <IconTimes
                tooltip="Cancel Editing"
                className="cursor-pointer opacity-50"
                onClick={() => setEditing(false)}
              />
            </>
          ) : canEdit ? (
            <IconEdit className="cursor-pointer text-charcoal-text" tooltip="Edit" onClick={edit} />
          ) : null}
          {!editing &&
            canEdit &&
            teacher.deletable &&
            (teacher.deleted ? (
              <IconTrashRestore
                className="cursor-pointer text-charcoal-text"
                tooltip="Restore User"
                onClick={() => toggleActive(true)}
              />
            ) : (
              <IconTrash
                className="cursor-pointer text-charcoal-text"
                tooltip="Delete User"
                onClick={() => toggleActive(false)}
              />
            ))}
        </>
      }
    >
      {editing ? (
        <TextInput
          icon={<IconEmail />}
          blue
          value={email}
          onChange={setEmail}
          placeholder="email@example.com"
          className="max-w-[325px]"
          invalid={emailInvalid}
          validation="email"
        />
      ) : (
        <div className="flex items-center gap-2">
          <IconEmail className="text-slate-300" />
          {teacher.email}
        </div>
      )}
      {editing ? (
        <TextInput
          blue
          value={phoneNumber}
          onChange={setPhoneNumber}
          placeholder="xxx-xxx-xxxx"
          icon={<IconPhone />}
          className="max-w-[325px]"
          invalid={phoneNumberInvalid}
          validation="phone"
        />
      ) : (
        displayPhoneNumber && (
          <div className="flex items-center gap-2">
            <IconPhone className="text-slate-300" />
            {teacher.phoneData.status === 'reachable' ? (
              displayPhoneNumber
            ) : teacher.phoneData.status === 'landline' ? (
              <Tooltip text={`${displayPhoneNumber}  is a landline number. We cannot text it.`}>
                <span className="text-purple-500">{displayPhoneNumber}</span>
              </Tooltip>
            ) : teacher.phoneData.status === 'bounced' ? (
              <Tooltip text={`${displayPhoneNumber} is not a reachable phone number`}>
                <span className="text-red-500">{displayPhoneNumber}</span>
              </Tooltip>
            ) : null}
          </div>
        )
      )}
      {editing && (
        <div className="flex flex-col items-start gap-2">
          {teacher.forwardMessagesToEmail && teacher.forwardSharedMessagesToEmail ? (
            <LabeledCheckbox
              checked
              disabled
              label="Receives all messages by email"
              tooltip="You cannot edit this teacher's preference for them"
              charcoal
            />
          ) : (
            <>
              <LabeledCheckbox
                checked={teacher.forwardMessagesToEmail}
                disabled
                label="Receives messages by email"
                tooltip="You cannot edit this teacher's preference for them"
                charcoal
              />
              {teacher.forwardMessagesToEmail && (
                <LabeledCheckbox
                  checked={teacher.forwardSharedMessagesToEmail}
                  disabled
                  label="Receives shared messages by email"
                  tooltip="You cannot edit this teacher's preference for them"
                  charcoal
                />
              )}
            </>
          )}
          <LabeledCheckbox
            checked={teacher.phoneData.receiveCalls}
            disabled
            label="Receives broadcast calls"
            tooltip="You cannot edit this teacher's preference for them"
            charcoal
          />
          <a
            href="https://reachmyteach.zendesk.com/hc/en-us/articles/16154125468825-Editing-permissions-Granting-educators-different-permissions-or-elevating-to-administrator"
            target="_blank"
            rel="noreferrer"
            className="text-link"
          >
            Learn more about permissions
          </a>
          {canEditAdmin && admin < 2 && (
            <LabeledCheckbox
              label="Admin"
              checked={admin > 0}
              onChecked={async (v) => {
                const msg = v
                  ? 'Are you sure you want to grant admin access to this user?'
                  : 'Are you sure you want to remove admin access from this user?'
                if (await Popups.simpleConfirm(msg)) setAdmin(v ? 1 : 0)
              }}
            />
          )}
          {canEditAdminPlus && admin > 0 && (
            <LabeledCheckbox
              label="Admin+"
              checked={admin === 2}
              onChecked={async (v) => {
                const msg = v
                  ? 'Are you sure you want to grant admin+ access to this user?'
                  : 'Are you sure you want to remove admin+ access from this user?'
                if (await Popups.simpleConfirm(msg)) setAdmin(v ? 2 : 1)
              }}
              tooltip="Admin+ users have all privileges of an admin, plus the ability to make other admins"
            />
          )}
          {canEditDistrictAccess && (
            <LabeledCheckbox
              label="District Access"
              tooltip="Let this teacher access the entire district"
              checked={districtAccess}
              onChecked={setDistrictAccess}
            />
          )}
          {teacher.schools.length > 0 && (
            <LabeledCheckbox
              label="Show All Classes"
              tooltip="Show this teacher all classes at their school"
              checked={showAllClasses}
              onChecked={setShowAllClasses}
            />
          )}
          <LabeledCheckbox
            label="Show Grade Groups"
            tooltip="Show entire grades as options in the recipient selector from this user's account"
            checked={showGradeGroups}
            onChecked={setShowGradeGroups}
          />
          {user.district.attendanceEnabled && (
            <LabeledCheckbox
              label="Show Attendance"
              tooltip="Let this teacher view and resolve attendance reports"
              checked={showAttendance}
              onChecked={setShowAttendance}
            />
          )}
          {teacher.canEditExternalStaff && admin === 0 && (
            <LabeledCheckbox label="External Staff" checked={externalStaff} onChecked={setExternalStaff} />
          )}
          {admin === 0 && user.district.massCallingEnabled && (
            <LabeledCheckbox label="Allow Broadcasting" checked={broadcastAdmin} onChecked={setBroadcastAdmin} />
          )}
          {admin === 0 && teacher.atLexikeetSchool && (
            <LabeledCheckbox label="Allow Lexikeet Access" checked={lexikeetAdmin} onChecked={setLexikeetAdmin} />
          )}
          {admin === 0 && user.district.ptcEnabled && (
            <LabeledCheckbox label="PTC Admin" checked={ptcAdmin} onChecked={setPtcAdmin} />
          )}
          {admin === 0 && user.district.assessmentsEnabled && (
            <LabeledCheckbox
              label="Document Delivery Access"
              checked={assessmentAdmin}
              onChecked={setAssessmentAdmin}
            />
          )}
          {canEditSchools && (
            <>
              <div
                className="flex cursor-pointer items-center gap-1 text-slate-300"
                onClick={() => setSchoolEditOpen((o) => !o)}
              >
                {schoolEditOpen ? <IconCaretUp /> : <IconCaretRight />}
                <div className="text-sm text-slate-400">Manage School Enrollment</div>
              </div>
              {schoolEditOpen && (
                <div className="flex flex-col gap-1">
                  {teacher.editSchoolOptions.map((s) => (
                    <LabeledCheckbox
                      key={s.id}
                      label={s.name}
                      checked={schools.has(s.id)}
                      disabled={!s.editable}
                      onChecked={(v) => {
                        const newSchools = new Set(schools)
                        if (v) newSchools.add(s.id)
                        else newSchools.delete(s.id)
                        setSchools(newSchools)
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {teacher.classes.length > 0 && (
        <div className="-mt-1 flex flex-col gap-1">
          <Divider className="cursor-pointer" onClick={() => setClassesOpen(!classesOpen)}>
            {classesOpen ? <IconCaretUp /> : <div className="text-slate-500">CLASSES</div>}
          </Divider>
          {classesOpen && (
            <div className="flex flex-col gap-1 text-sm">
              {teacher.classes.map((c) => (
                <div key={c.id} className="flex justify-between">
                  <span className="flex-grow rounded bg-gray-50 px-2 hover:bg-gray-100">{c.name}</span>
                  <RemoteHTMLTooltip
                    loadFrom={{
                      url: '/classInfo',
                      data: {
                        class: c.id,
                      },
                    }}
                  >
                    <span className="rounded-r bg-charcoal-text px-2 font-bold text-white">{c.studentCount}</span>
                  </RemoteHTMLTooltip>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </GenericContact>
  )
}
